import { useState, useEffect, useRef } from "react";
import { Habit, useHabits } from "@/context/SupabaseHabitContext";
import { format, eachDayOfInterval, addMonths, getDay, isFirstDayOfMonth, startOfMonth, subMonths, isToday } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { getLocalDateString } from "@/utils/dateUtils";

interface CompactHabitGridProps {
  habit: Habit;
}

export default function CompactHabitGrid({ habit }: CompactHabitGridProps) {
  const [gridData, setGridData] = useState<(Date | null)[][]>([]);
  const [months, setMonths] = useState<{name: string, index: number}[]>([]);
  const [monthsToShow, setMonthsToShow] = useState(2);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const { pinnedHabits } = useHabits();

  // Define sizing constants first
  const cellWidth = isMobile ? 8 : 10; // Pixel values for calculations
  const cellHeight = isMobile ? 8 : 10;
  const gapSize = 1; // 1px gap
  const weekdayWidth = isMobile ? 12 : 16;
  const weekdayMargin = 4;
  const rowHeight = isMobile ? "9px" : "11px"; // cell height + gap

  // Calculate how many weeks can fit based on container width and pinned habits count
  useEffect(() => {
    const calculateMonthsToShow = () => {
      if (!containerRef.current) {
        // Very aggressive fallback to fill all available space
        const baseFallbackMonths = isMobile 
          ? (pinnedHabits.length === 1 ? 5 : pinnedHabits.length === 2 ? 4 : pinnedHabits.length === 3 ? 3 : 2)
          : (pinnedHabits.length === 1 ? 6 : pinnedHabits.length === 2 ? 5 : pinnedHabits.length === 3 ? 4 : 3);
        
        // Apply week reduction to fallback values too
        const weeksToSubtract = 3.5;
        const fallbackWeeks = baseFallbackMonths * 4.3; // Convert months to weeks
        const adjustedFallbackWeeks = Math.max(2, fallbackWeeks - weeksToSubtract);
        const adjustedFallbackMonths = Math.max(2, Math.ceil(adjustedFallbackWeeks / 4.3));
        
        console.log(`CompactHabitGrid FALLBACK - baseFallbackMonths: ${baseFallbackMonths}, weeksToSubtract: ${weeksToSubtract}, adjustedFallbackMonths: ${adjustedFallbackMonths}`);
        
        setMonthsToShow(adjustedFallbackMonths);
        return;
      }
      
      const containerWidth = containerRef.current.offsetWidth;
      const weekdayColumnWidth = weekdayWidth + weekdayMargin;
      
              // Fine-tuned buffer sizes based on testing
        const bufferSize = pinnedHabits.length === 1 ? 2 : pinnedHabits.length === 2 ? 2 : pinnedHabits.length === 3 ? 10 : 12;
      const availableWidth = containerWidth - weekdayColumnWidth - bufferSize;
      
      // Each week needs: cellWidth + gapSize
      const weekWidth = cellWidth + gapSize;
      const maxWeeks = Math.floor(availableWidth / weekWidth);
      
              // Optimized constraints based on actual behavior
        let minWeeks, maxWeeksLimit;
        const weeksReduction = 3.5; // Reduction amount: 3.5 weeks to avoid rounding issues
        
        if (pinnedHabits.length === 1) {
          minWeeks = 16;  // Very aggressive for single habit - fill all space
          maxWeeksLimit = 30;
        } else if (pinnedHabits.length === 2) {
          minWeeks = 14;  // More aggressive for 2 habits to fill space
          maxWeeksLimit = 26;
        } else if (pinnedHabits.length === 3) {
          minWeeks = 8;   // More conservative for 3 habits to prevent overflow
          maxWeeksLimit = 14;
        } else {
          minWeeks = 6;   // Keep same for 4 habits (working well)
          maxWeeksLimit = 14;
        }
      
      // Apply constraints first
      const constrainedWeeks = Math.max(minWeeks, Math.min(maxWeeksLimit, maxWeeks));
      
      // Then force the week reduction at the end to guarantee it happens
      const weeksAfterReduction = Math.max(2, constrainedWeeks - weeksReduction);
      const calculatedMonths = Math.max(2, Math.ceil(weeksAfterReduction / 4.3));
      
      console.log(`CompactHabitGrid MAIN - constrainedWeeks: ${constrainedWeeks}, weeksReduction: ${weeksReduction}, weeksAfterReduction: ${weeksAfterReduction}, calculatedMonths: ${calculatedMonths}`);
      
      setMonthsToShow(calculatedMonths);
    };

    // Longer initial delay to ensure CSS grid layout is complete
    const timeoutId = setTimeout(calculateMonthsToShow, 200);
    
    // Additional recalculation after CSS transitions/animations complete
    const fallbackTimeoutId = setTimeout(calculateMonthsToShow, 500);
    
    // Recalculate on resize with appropriate delay
    const resizeObserver = new ResizeObserver(() => {
      setTimeout(calculateMonthsToShow, 100);
    });
    
    // Watch for DOM changes in parent containers (like when pinned habits are added/removed)
    const mutationObserver = new MutationObserver(() => {
      setTimeout(calculateMonthsToShow, 200);
    });
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      
      // Observe changes in the parent container (pinned habits grid)
      const parentContainer = containerRef.current.closest('[class*="grid"]');
      if (parentContainer) {
        mutationObserver.observe(parentContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style']
        });
      }
    }
    
    return () => {
      clearTimeout(timeoutId);
      clearTimeout(fallbackTimeoutId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [isMobile, cellWidth, gapSize, weekdayWidth, weekdayMargin, habit.id, pinnedHabits.length]);

  // Generate grid data based on calculated months - going backwards from today
  useEffect(() => {
    const today = new Date();
    const startDate = startOfMonth(subMonths(today, monthsToShow - 1)); // Dynamic months
    const endDate = today; // End at today, no future dates
    
    // Generate all days in the interval
    const allDates = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    
    // Create data structure for grid
    const monthsData: {name: string, index: number}[] = [];
    const grid: (Date | null)[][] = Array(7).fill(null).map(() => []);
    
    // Process each date and organize by day of week
    let currentMonth = "";
    let weekIndex = 0;
    
    // Fill the grid with weeks
    allDates.forEach((date) => {
      const dayOfWeek = getDay(date);
      
      if (dayOfWeek === 0 && grid[0].length > 0) {
        weekIndex++;
      }
      
      if (isFirstDayOfMonth(date)) {
        const monthName = format(date, 'MMM');
        if (monthName !== currentMonth) {
          currentMonth = monthName;
          monthsData.push({
            name: monthName,
            index: weekIndex
          });
        }
      }
      
      for (let i = 0; i < 7; i++) {
        while (grid[i].length <= weekIndex) {
          grid[i].push(null);
        }
      }
      
      grid[dayOfWeek][weekIndex] = date;
    });
    
    setGridData(grid);
    setMonths(monthsData);
  }, [habit.completedDates, monthsToShow]);

  // Check if a date is completed
  const isDateCompleted = (date: Date | null): boolean => {
    if (!date) return false;
    const dateString = getLocalDateString(date);
    return habit.completedDates.includes(dateString);
  };

  // Weekday labels (abbreviated)
  const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  if (gridData.length === 0 || monthsToShow === 0) {
    return <div className="animate-pulse bg-muted h-16 rounded w-full"></div>;
  }

  return (
    <div className="compact-habit-grid w-full overflow-hidden" ref={containerRef} title={`Showing ${monthsToShow} months`}>
      {/* Month labels */}
      <div className="relative mb-1" style={{ height: '16px' }}>
        {months.map((month) => {
          // Calculate exact position: weekday column + margin + (week index * (cell width + gap))
          const leftPosition = weekdayWidth + weekdayMargin + (month.index * (cellWidth + gapSize));
          
          return (
            <div
              key={month.name}
              className="absolute text-[10px] text-muted-foreground font-medium"
              style={{ 
                left: `${leftPosition}px`,
                top: '0px'
              }}
            >
              {month.name}
            </div>
          );
        })}
      </div>
      
      <div className="flex mt-4 w-full overflow-hidden">
        {/* Weekday labels */}
        <div className={`flex flex-col ${isMobile ? 'w-3' : 'w-4'} mr-1 flex-shrink-0`}>
          {weekdays.map((day, index) => (
            <div 
              key={index} 
              className="flex items-center justify-center text-[10px] text-muted-foreground"
              style={{ 
                height: rowHeight, 
                marginBottom: "1px",
                minHeight: rowHeight,
                maxHeight: rowHeight,
                boxSizing: 'border-box'
              }}
            >
              {day}
            </div>
          ))}
        </div>
        
        {/* Grid cells - fill remaining space */}
        <div className="flex flex-col flex-1 min-w-0">
          {gridData.map((row, rowIndex) => (
            <div 
              key={`row-${rowIndex}`} 
              className="flex gap-[1px] mb-[1px]" 
              style={{ 
                height: rowHeight,
                minHeight: rowHeight,
                maxHeight: rowHeight,
                alignItems: 'flex-start',
                boxSizing: 'border-box'
              }}
            >
              {row.map((date, colIndex) => {
                const isCompleted = isDateCompleted(date);
                const glowEffect = isCompleted ? "shadow-[0_0_3px_0px_rgba(255,255,255,0.2)]" : "";
                const isTodayDate = date ? isToday(date) : false;
                const todayBorder = isTodayDate ? "ring-1 ring-primary ring-offset-[0.5px] ring-offset-background" : "";
                
                return (
                  <div
                    key={`cell-${rowIndex}-${colIndex}`}
                    className={`rounded-[1.7px] transition-all ${todayBorder}`}
                    style={{ 
                      backgroundColor: isCompleted ? habit.color.filled : habit.color.empty,
                      visibility: date ? 'visible' : 'hidden',
                      width: `${cellWidth}px`,
                      height: `${cellHeight}px`,
                      minWidth: `${cellWidth}px`,
                      minHeight: `${cellHeight}px`,
                      maxWidth: `${cellWidth}px`,
                      maxHeight: `${cellHeight}px`,
                      flexShrink: 0,
                      flexGrow: 0,
                      boxSizing: 'border-box'
                    }}
                    title={date ? `${format(date, 'MMM d, yyyy')} - ${isCompleted ? 'Completed' : 'Not completed'}${isTodayDate ? ' (Today)' : ''}` : ''}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 